import React, { FunctionComponent, useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import queryString from 'query-string';
import { Container, Grid, Button } from 'semantic-ui-react';
import withStyles from 'react-jss';
import { compose } from 'recompose';
import { Storage } from 'aws-amplify';
import { pathOr } from 'ramda';
import { saveAs } from 'file-saver';

import { WithRouterProps, ClassProp } from 'helpers/types';
import { CardNetwork } from 'scenes/CSVExport/CSVExport.helper';
import { CSVPlaceholder } from 'components/Placeholder/presets';
import Breadcrumb from 'components/Breadcrumb';
import { useMutation } from 'helpers/useMutation';

import {
  EXPORT_REQUESTS_MASTERCARD,
  EXPORT_REQUEST_ACTIVATION_DEACTIVATION,
} from './CardNetworkExport.mutations';
import styles from './CardNetworkExport.styles';

type CardNetworkExportParams = {
  limit: string;
  cardNetwork: CardNetwork;
  processType: string;
};

type Response = {
  requestFileName: string;
  exportedJobsCount: number;
  failedJobsCount: number;
};

type QueryResponse = {
  exportCNVRetrievalRequestsMastercard?: Response;
  exportTransFlowRequestsMastercard?: Response;
};

type Styles = ClassProp<ReturnType<typeof styles>>;

type Props = WithRouterProps & {
  classes: Styles;
};

type ProcessTypeConversion = {
  [index: string]: string;
};

const processTypeConversion: ProcessTypeConversion = {
  ACTIVATION_DOSH: 'ACTIVATE',
  DEACTIVATION_DOSH: 'DEACTIVATE',
  ACTIVATION_REWARD_NETWORKS: 'ACTIVATE',
  DEACTIVATION_REWARD_NETWORKS: 'DEACTIVATE',
};

const getS3File = async (
  fileName: string,
  processType: string,
  cardNetwork: string,
  setS3File: (file: Blob) => void,
) => {
  const file = await Storage.get(
    `onboardingAutomation/${processType
      .split('_')[0]
      .toLowerCase()}/${cardNetwork.toLowerCase()}/request/${fileName}`,
    {
      download: true,
      bucket: process.env.CN_BUCKET,
      region: process.env.AWS_REGION,
    },
  );

  setS3File(file.Body);
};

const getQuery = {
  MASTERCARD: {
    RETRIEVAL: EXPORT_REQUESTS_MASTERCARD,
    ACTIVATION_DOSH: EXPORT_REQUEST_ACTIVATION_DEACTIVATION,
    DEACTIVATION_DOSH: EXPORT_REQUEST_ACTIVATION_DEACTIVATION,
    ACTIVATION_REWARD_NETWORKS: EXPORT_REQUEST_ACTIVATION_DEACTIVATION,
    DEACTIVATION_REWARD_NETWORKS: EXPORT_REQUEST_ACTIVATION_DEACTIVATION,
  },
};

const renderCardNetworkExport = (
  {
    exportCNVRetrievalRequestsMastercard,
    exportTransFlowRequestsMastercard,
  }: QueryResponse,
  classes: Styles,
  processType: string,
  cardNetwork: CardNetwork,
  downloadFile: (event: any) => void,
) => {
  const { exportedJobsCount } = exportCNVRetrievalRequestsMastercard ||
    exportTransFlowRequestsMastercard || {
      exportedJobsCount: 0,
    };

  return (
    <>
      <Breadcrumb
        disabledRoutes={['cn-onboarding']}
        className={classes.breadcrumb}
      />

      <Container className={classes.container}>
        <Grid container>
          <Grid.Row>
            <Grid.Column width={16} className={classes.label}>
              Card Network
            </Grid.Column>

            <Grid.Column width={16} className={classes.value}>
              {cardNetwork}
            </Grid.Column>
          </Grid.Row>

          <Grid.Row>
            <Grid.Column width={16} className={classes.label}>
              Process Type
            </Grid.Column>

            <Grid.Column width={16} className={classes.value}>
              {processType}
            </Grid.Column>
          </Grid.Row>

          <Grid.Row>
            <Grid.Column width={16} className={classes.label}>
              Records in file
            </Grid.Column>
            <Grid.Column width={16} className={classes.value}>
              {exportedJobsCount}
            </Grid.Column>
          </Grid.Row>

          <Button
            onClick={downloadFile}
            className={classes.button}
            as="a"
            download
            icon
          >
            Download
          </Button>
        </Grid>
      </Container>
    </>
  );
};

const downloadFileHandler = (fileBlob: Blob, filename: string) => () => {
  const csvFile = new Blob([fileBlob], {
    type: 'text/csv',
  });

  saveAs(csvFile, `${filename}.csv`);
};

const CardNetworkExport: FunctionComponent<Props> = ({ location, classes }) => {
  const [fileBlob, setS3File] = useState<Blob | null>(null);
  const [s3Filename, setS3Filename] = useState<string>('');
  const { limit, cardNetwork, processType } = (queryString.parse(
    location.search,
  ) as unknown) as CardNetworkExportParams;

  const QUERY = pathOr(
    EXPORT_REQUESTS_MASTERCARD,
    [cardNetwork, processType],
    getQuery,
  );

  const { runMutation, data, loading } = useMutation(QUERY);

  const nonDoshMerchants = ['R21', 'RN'];
  const doshMerchants = ['DOSH'];
  let requestedForVenueSources;
  switch (processType) {
    case 'ACTIVATION_DOSH':
    case 'DEACTIVATION_DOSH':
      requestedForVenueSources = doshMerchants;
      break;
    case 'DEACTIVATION_REWARD_NETWORKS':
    case 'ACTIVATION_REWARD_NETWORKS':
      requestedForVenueSources = nonDoshMerchants;
      break;
    case null:
      requestedForVenueSources = null;
      break;
    default:
      requestedForVenueSources = [...nonDoshMerchants, ...doshMerchants];
      break;
  }

  const { key, params } =
    processType === 'RETRIEVAL'
      ? {
          params: { limit: Number(limit) },
          key: 'exportCNVRetrievalRequestsMastercard',
        }
      : {
          params: {
            requestedForVenueSources,
            mode: processTypeConversion[processType],
          },
          key: 'exportTransFlowRequestsMastercard',
        };

  useEffect(() => {
    runMutation({
      variables: {
        params,
      },
    });
  }, []);

  useEffect(() => {
    if (data) {
      const fileName = pathOr('', [key, 'requestFileName'], data);

      setS3Filename(fileName);
      getS3File(fileName, processType, cardNetwork, setS3File);
    }
  }, [data]);

  if (loading || !fileBlob) {
    return <CSVPlaceholder />;
  }

  if (data) {
    return renderCardNetworkExport(
      data,
      classes,
      processType,
      cardNetwork,

      downloadFileHandler(fileBlob, s3Filename),
    );
  }

  return null;
};

export default compose<Props, any>(
  withRouter,
  withStyles(styles),
)(CardNetworkExport);
